import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { Palette } from 'core/config';
import { login, useAuthorizationDispatch } from 'core/context';
import { Data, isChained, isEmail, isRequired } from 'core/utilities';
import IMAGES from 'assets/images';
import { Button } from 'view/components/common';
import { useToast } from 'core/toast';
import { useRedirectUrl } from 'view/hooks';

type FormValues = {
    username : string; 
    password: string;
};

export const LoginPage : React.FunctionComponent = () => {
    const history = useHistory();
    const { getRedirectUrl } = useRedirectUrl();
    const dispatch = useAuthorizationDispatch();
    const [ loading, setLoading ] = useState<boolean>(false);
    const [ formErrors, setFormErrors ] = useState<Data>({});
    const [ canContinue, setCanContinue ] = useState<boolean>(false);
    const [ redirectUrl, setRedirectUrl ] = useState('');
    const { setToastMessage } = useToast();
    const [ values, setValues ] = useState<FormValues>({ 
        username : '', 
        password : '' 
    });

    useEffect(() => {
        const redirectUrl = getRedirectUrl();
        setRedirectUrl(redirectUrl);
    }, []);

    useEffect(() => {
        const { username, password } = formErrors;
        setCanContinue(username === '' && password === '');
    }, [ formErrors, setCanContinue ]);

    const onSubmit = async (e: SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();

        setLoading(true);
        const { username, password } = values;

        const {
            success
        } = await login({ 
            dispatch, 
            payload : {
                username : username.toLowerCase(),
                password
            }
        });

        if (success) {
            history.push(redirectUrl);
        } else {
            setLoading(false);
            setToastMessage('Invalid username or password. Pleast try again.');
        }
    };

    const onChange = (name : string, validator : any) => {
        return (e : React.ChangeEvent<HTMLInputElement>) => {
            const error = validator(e.target.value);
            setFormErrors && setFormErrors(errors => ({ ...errors, [name]: error }));
            setValues(values => ({ ...values, [name]: e.target.value }));
        };
    };

    return (
        <Form autoComplete='off' onSubmit={onSubmit}>
            <Container>
                <Image src={IMAGES.sheepWithCoins} />
                <Title>
                    Log in to Manzil
                </Title>
                <Subtitle>
                    Let's grow your money, the Halal way.
                </Subtitle>
                <Subtext>
                    Invest in Manzil Halal Portfolios or apply for your Halal home. {'\n'}
                    It's fast and easy, Insha Allah.
                </Subtext>
                <Inputs>
                    <InputsWrapper>
                        <InputLabel>
                            Email 
                        </InputLabel>
                        <InputContainer>
                            <InputIcon src={IMAGES.icEmail} />
                            <Input
                                placeholder='salaam@manzil.ca'
                                onChange={onChange('username', isChained([ isEmail, isRequired ]))}
                                disabled={loading}
                                autoComplete='off'
                            />
                        </InputContainer>
                        <InputLabel>
                            Password 
                        </InputLabel>
                        <InputContainer>
                            <InputIcon src={IMAGES.icPadlock} />
                            <Input 
                                type='password'
                                placeholder='*******************'
                                onChange={onChange('password', isRequired)}
                                disabled={loading}
                                autoComplete='off'
                            />
                        </InputContainer>
                        <OptionContainer>
                            {/* <RememberMe onClick={() => !loading && setRememberMe(v => !v)}>
                                {rememberMe && <FilledIn />}
                            </RememberMe>
                            <Text css={'flex-grow: 1; line-height: 28px; padding-left: 10px;'}>Remember me</Text> */}
                            <Text css={'flex-grow: 1;'} />
                            <Link css={'line-height: 28px;'} onClick={() => history.push('/forgot-password')}>Forgot your password?</Link>
                        </OptionContainer>
                    </InputsWrapper>
                    <Button
                        title='Log In'
                        type='submit'
                        disabled={!canContinue || loading}
                        css={'width: 310px;'}
                        arrowCss={'display: none;'}
                    />
                    <RegisterContainer>
                        <Text>
                            Don't have an account?
                        </Text>
                        <Link 
                            onClick={() => history.push('/register')}
                            css={'font-size: 0.9rem; padding-left: 7px;'}
                        >
                            Register
                        </Link>
                    </RegisterContainer>
                </Inputs>
            </Container>
        </Form>
    );
};

const Form = styled.form`
    width: 100%;
    height: calc(100vh - 50px);
    padding-top: 50px;

    @media only screen and (max-width: 600px) {
        height: 100vh;
        padding-top: 0;
    }
`;

const Container = styled.div`
    font: normal normal normal 10px 'SofiaPro-Regular';
    background-color: #F4F1DE;
    width: 450px;
    height: 675px;
    margin-left: auto;
    margin-right: auto;
    border-radius: 22px;

    @media only screen and (max-width: 600px) {
        width: 100%;
        height: 100vh;
        border-radius: 0;
    }
`;

const Inputs = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const InputsWrapper = styled.div`
    width: 310px;
`;

const InputContainer = styled.div`
    background-color: #F6F3F3;
    width: 310px;
    height: 45px;
    border-radius: 7px;
    display: flex;
    flex-direction: row;
    position: relative;
`;

const InputLabel = styled.div`
    align-self: flex-start;
    font-size: 0.8rem;
    padding-bottom: 5px;
    padding-left: 3px;
    padding-top: 28px;
`;

const Input = styled.input`
    background-color: transparent;
    border: none;
    height: 96%;
    width: 100%;
    padding-left: 40px;
    padding-right: 20px;
    color: ${Palette.BLUE};
    font-size: 12px;
    font-family: 'SofiaPro-Regular';

    :focus {
        outline: none;
    }

    ::placeholder {
        opacity: 0.2;
    }
`;

const InputIcon = styled.img`
    width: 20px;
    height: 20px;
    position: absolute;
    top: 0;
    bottom: 0;
    margin: auto;
    padding-left: 10px;
    filter: invert(23%) sepia(6%) saturate(2714%) hue-rotate(197deg) brightness(97%) contrast(88%);
`;

const OptionContainer = styled.div`
    display: flex; 
    flex-direction: row;
    width: 100%;
    font-size: 0.8rem;
    font-weight: 100;
    margin-top: 15px;
    margin-bottom: 10px;
`;

// const RememberMe = styled.div`
//     background-color: #F6F3F3;
//     border-radius: 8px;
//     width: 28px;
//     height: 28px;
//     cursor: pointer;
// `;

// const FilledIn = styled.div`
//     background-color: #E26E5A;
//     width: 20px;
//     height: 20px;
//     display: flex;
//     margin-left: 4px;
//     margin-top: 4px;
//     border-radius: 4px;
// `;

const Link = styled.div<{ css? : string; }>`
    color: ${Palette.BLUE};
    text-decoration: underline;
    user-select: none;
    cursor: pointer;
    text-align: right;
    font-size: 0.8rem;

    ${({ css }) => css && css}
`;

const Title = styled.div`
    text-align: center;
    color: #E26E5A;
    margin-top: -16px;
    font-size: 1.35rem;
    font-weight: bold;
    user-select: none;
`;

const Subtitle = styled.div`
    text-align: center;
    color: #3D405B;
    margin-top: 20px;
    font-size: 1.25rem;
    font-weight: 900;
    white-space: nowrap;
    user-select: none;

    @media only screen and (max-width: 500px) {
        font-size: 1.09rem;
    }
`;

const Subtext = styled.div`
    text-align: center;
    color: #3D405B;
    font-size: 0.725rem;
    white-space: pre-line;
    margin-top 5px;
    user-select: none;

    @media only screen and (max-width: 500px) {
        font-size: 0.7rem;
    }
`;

const Image = styled.img`
    width: 14rem;
    display: block;
    margin-left: auto;
    margin-right: auto;
`;

const RegisterContainer = styled.div`
    display: flex;
    flex-direction: row;
    margin-top: 12px;
    font-size: 0.9rem !important;
    padding-bottom: 50px;
`;

const Text = styled.div<{ css? : string; }>`
    user-select: none;
    ${({ css }) => css && css}
`;